import React, {useContext, useEffect, useMemo, useState} from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {AuthContext} from "../../config/AuthContext";
import NavBar from "../components/NavBar";
import Login from "../root/Login";
import {Button, Card, DatePicker, Input} from "antd";
import {BiRefresh} from "react-icons/bi";
import Historials from "../components/Historials";
import {GetParticipants} from "../../dao/menudesign/GetParticipants";
import dayjs, {Dayjs} from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import toast, {Toaster} from "react-hot-toast";

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

// Retorna el dilluns i divendres d'una setmana ISO donada (com el Calendar de Java amb Locale.FRANCE)
function getWeekRange(week: Dayjs): { inici: string; fin: string } {
    const inici = week.startOf('isoWeek');
    const fin = week.endOf('isoWeek').subtract(2, 'day'); // dilluns → divendres
    return {
        inici: inici.format('YYYY-MM-DD'),
        fin: fin.format('YYYY-MM-DD'),
    };
}

// Dona format dd-MM-yyyy a una data ISO
function formatDia(iso: string): string {
    return dayjs(iso).format('DD-MM-YYYY');
}

// Nom curt del dia de la setmana
function nomDia(iso: string): string {
    const noms = ['Diumenge', 'Dilluns', 'Dimarts', 'Dimecres', 'Dijous', 'Divendres', 'Dissabte'];
    return noms[dayjs(iso).day()];
}

interface ParticipantRow {
    escola: string;
    dia: string;
    infantil: number;
    primaria: number;
    adult: number;
}

// Agrupa les files planes [escola, dia, ...] en estructura { escola → { dia → {infantil, primaria, adult} } }
function agrupar(dades: ParticipantRow[]): Map<string, Map<string, { infantil: number; primaria: number; adult: number }>> {
    const result = new Map<string, Map<string, { infantil: number; primaria: number; adult: number }>>();
    for (const row of dades) {
        if (!result.has(row.escola)) result.set(row.escola, new Map());
        result.get(row.escola)!.set(row.dia, {
            infantil: row.infantil,
            primaria: row.primaria,
            adult: row.adult,
        });
    }
    return result;
}

const Participants = () => {
    const titol = "Gestió de participants";
    const {isLoggedIn, user, page} = useContext(AuthContext);

    const [dades, setDades] = useState<ParticipantRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [setmana, setSetmana] = useState<Dayjs>(dayjs());
    const [cerca, setCerca] = useState('');

    // L'usuari "superusuari" veu la fila TOTALS (equivalent al check "josep" del JSP)
    const esSuperusuari = user?.rol?.some((rol: any) => rol.nom === 'superusuari');

    // Escoles permeses per a l'usuari (id_loop) — equivalent a usuari.getPermisosIdLoop()
    const escolesPermeses: Set<string> = useMemo(() => {
        if (esSuperusuari) return new Set(); // buit = totes
        return new Set((user.escoles || []).map((e: any) => e.id_loop as string));
    }, [user, esSuperusuari]);

    useEffect(() => {
        if (isLoggedIn) carregarDades();
    }, [setmana, isLoggedIn]);

    async function carregarDades() {
        setLoading(true);
        const {inici, fin} = getWeekRange(setmana);
        const resultat = await GetParticipants(page, inici, fin);
        setDades(resultat);
        setLoading(false);
    }

    // Dies únics ordenats de la setmana actual
    const dies: string[] = useMemo(() => {
        const set = new Set(dades.map(r => r.dia));
        return Array.from(set).sort();
    }, [dades]);

    // Estructura agrupada
    const agrupat = useMemo(() => agrupar(dades), [dades]);

    // Escoles filtrades per cerca i per permisos
    const escoles: string[] = useMemo(() => {
        return Array.from(agrupat.keys())
            .filter(e => esSuperusuari || escolesPermeses.has(e))
            .filter(e => e.toLowerCase().includes(cerca.toLowerCase()))
            .sort();
    }, [agrupat, cerca, esSuperusuari, escolesPermeses]);

    // Cel·la amb Infantil / Primaria / Adults / Total
    function renderCella(vals: { infantil: number; primaria: number; adult: number } | undefined, bold = false) {
        const i = vals?.infantil ?? 0;
        const p = vals?.primaria ?? 0;
        const a = vals?.adult ?? 0;
        const total = i + p + a;
        const fw = bold ? 'bold' : 'normal';
        return (
            <td key={Math.random()} style={{minWidth: 140, padding: '6px 8px', verticalAlign: 'top'}}>
                <div style={{display: 'flex', gap: 4, marginBottom: 2}}>
                    <div style={{flex: 1, textAlign: 'center'}}>
                        <div style={{color: '#555'}}>Infantil</div>
                        <div style={{
                            border: '1px solid #d9d9d9', borderRadius: 4,
                            padding: '2px 6px', fontWeight: fw
                        }}>{i}</div>
                    </div>
                    <div style={{flex: 1, textAlign: 'center'}}>
                        <div style={{color: '#555'}}>Primaria</div>
                        <div style={{
                            border: '1px solid #d9d9d9', borderRadius: 4,
                            padding: '2px 6px', fontWeight: fw
                        }}>{p}</div>
                    </div>
                    <div style={{flex: 1, textAlign: 'center'}}>
                        <div style={{color: '#555'}}>Adults</div>
                        <div style={{
                            border: '1px solid #d9d9d9', borderRadius: 4,
                            padding: '2px 6px', fontWeight: fw
                        }}>{a}</div>
                    </div>
                </div>
                <div style={{
                    border: '1px solid #d9d9d9', borderRadius: 4,
                    textAlign: 'center', padding: '2px 6px',
                    fontWeight: bold ? 'bold' : 'normal',
                    color: bold ? 'red' : 'inherit',
                }}>{total}</div>
            </td>
        );
    }

    // Totals per escola (suma de tots els dies)
    function totalsEscola(escola: string) {
        const dies_escola = agrupat.get(escola);
        if (!dies_escola) return {infantil: 0, primaria: 0, adult: 0};
        let infantil = 0, primaria = 0, adult = 0;
        dies_escola.forEach(v => { infantil += v.infantil; primaria += v.primaria; adult += v.adult; });
        return {infantil, primaria, adult};
    }

    // Totals per dia (suma de totes les escoles visibles)
    function totalsPerDia(dia: string) {
        let infantil = 0, primaria = 0, adult = 0;
        for (const escola of escoles) {
            const v = agrupat.get(escola)?.get(dia);
            if (v) { infantil += v.infantil; primaria += v.primaria; adult += v.adult; }
        }
        return {infantil, primaria, adult};
    }

    // Total global de la setmana
    function totalGlobal() {
        let infantil = 0, primaria = 0, adult = 0;
        for (const escola of escoles) {
            const t = totalsEscola(escola);
            infantil += t.infantil; primaria += t.primaria; adult += t.adult;
        }
        return {infantil, primaria, adult};
    }

    if (!isLoggedIn) return <Login/>;

    return (
        <div>
            <Toaster/>
            <NavBar/>
            <div className="general">
                <Card bordered className="elemento">
                    {/* Barra de controls */}
                    <div className="actionButtons" style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4}}>
                        <Button
                            title="Refrescar"
                            style={{backgroundColor: '#52c41a'}}
                            loading={loading}
                            onClick={carregarDades}
                        >
                            <BiRefresh/>
                        </Button>

                        {/* Selector de setmana — equivalent al <input type="week"> del JSP */}
                        <DatePicker
                            picker="week"
                            value={setmana}
                            onChange={(v) => v && setSetmana(v)}
                            format={(v) => `${v.isoWeek()}ª setmana ${v.year()}`}
                            allowClear={false}
                            style={{width: 200}}
                        />

                        {/* Cerca per escola — equivalent al myInput del JSP */}
                        <Input
                            placeholder="Buscar escola..."
                            value={cerca}
                            onChange={e => setCerca(e.target.value)}
                            style={{width: 240}}
                            allowClear
                        />
                    </div>

                    <h4 className="titol">{titol}</h4>

                    <div style={{color: '#888', marginBottom: 12}}>
                        Setmana del {getWeekRange(setmana).inici} al {getWeekRange(setmana).fin}
                    </div>

                    {/* Taula principal */}
                    <div style={{overflowX: 'auto'}}>
                        <table className="table table-bordered table-striped text-center"
                               style={{tableLayout: 'auto'}}>
                            <thead>
                                <tr style={{backgroundColor: '#fafafa'}}>
                                    <th style={{minWidth: 140, textAlign: 'left'}}>Escola</th>
                                    {dies.map(dia => (
                                        <th key={dia} style={{minWidth: 120}}>
                                            <div>{nomDia(dia)}</div>
                                            <div style={{fontWeight: 'normal', fontSize: '85%', color: '#666'}}>
                                                {formatDia(dia)}
                                            </div>
                                        </th>
                                    ))}
                                    <th style={{minWidth: 120, fontWeight: 'bold', backgroundColor: '#f0f5ff'}}>
                                        TOTAL SETMANA
                                    </th>
                                </tr>
                                {/* Fila resum de categories — equivalent a la fila RESUM del JSP */}
                                <tr>
                                    <td style={{fontWeight: 'bold', textAlign: 'left'}}>RESUM</td>
                                    <td colSpan={dies.length + 1} style={{textAlign: 'left', padding: '4px 8px'}}>
                                        <b>Infantil:</b> P3, P4, P5 i 1er &nbsp;|&nbsp;
                                        <b>Primaria:</b> 2on, 3er i 4rt &nbsp;|&nbsp;
                                        <b>Adults:</b> 5è, 6è, ESO, Professors
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                {escoles.length === 0 ? (
                                    <tr>
                                        <td colSpan={dies.length + 2} style={{padding: 20, color: '#888'}}>
                                            No hi ha resultats a mostrar
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {escoles.map(escola => (
                                            <tr key={escola}>
                                                <td style={{fontWeight: 'bold', textAlign: 'left', verticalAlign: 'middle'}}>
                                                    {escola}
                                                </td>
                                                {dies.map(dia => (
                                                    renderCella(agrupat.get(escola)?.get(dia))
                                                ))}
                                                {renderCella(totalsEscola(escola), true)}
                                            </tr>
                                        ))}

                                        {/* Fila TOTALS — només per a superusuari (equivalent al check "josep" del JSP) */}
                                        {esSuperusuari && dies.length > 0 && (
                                            <tr style={{backgroundColor: '#f6ffed'}}>
                                                <td style={{fontWeight: 'bold', textAlign: 'left', verticalAlign: 'middle'}}>
                                                    TOTALS
                                                </td>
                                                {dies.map(dia => (
                                                    renderCella(totalsPerDia(dia), true)
                                                ))}
                                                {renderCella(totalGlobal(), true)}
                                            </tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
                <br/>
                <Historials pantalla="PARTICIPANTS" pagina={page}/>
            </div>
        </div>
    );
};

export default Participants;
